import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CloudArrowUp, CheckCircle, XCircle, FileCsv, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

import { API_BASE as API } from "../lib/apiBase";
import { useLang } from "../contexts/LanguageContext";

export default function KnowledgeImportPage() {
  const { tr } = useLang();
  const navigate = useNavigate();
  const [csvText, setCsvText] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [moduleName, setModuleName] = useState(tr("Module 1 - Connaissances générales", "Module 1 - General knowledge"));
  const [videoId, setVideoId] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState("file");  // default = direct file upload (new)
  const [csvFile, setCsvFile] = useState(null);

  // New (Vincent SMS 2026-05-02: "intégrer ce qu'Étienne a déjà produit"):
  // upload a .csv file directly (from Excel/Google Sheets export) without
  // copy-pasting. Uses the new /api/agent/knowledge-units/import-csv endpoint
  // which auto-detects delimiter (, ; | tab), auto-generates embeddings for
  // RAG search, and dedupes by exact question per-org.
  const downloadTemplate = async () => {
    try {
      const resp = await axios.get(
        `${API}/api/agent/knowledge-units/import-csv-template`,
        { withCredentials: true, responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "knoria_qa_template.csv";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(tr("Échec du téléchargement du modèle", "Template download failed"));
    }
  };

  const handleFileImport = async () => {
    if (!csvFile) { toast.error(tr("Sélectionnez un fichier .csv", "Pick a .csv file")); return; }
    setImporting(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const r = await axios.post(
        `${API}/api/agent/knowledge-units/import-csv`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult({ inserted: r.data.imported, skipped: r.data.skipped_duplicates, errors: r.data.errors });
      toast.success(tr(`${r.data.imported} Q&R importées, ${r.data.skipped_duplicates} doublons ignorés`, `${r.data.imported} Q&A imported, ${r.data.skipped_duplicates} duplicates skipped`));
    } catch (e) {
      toast.error(tr("Erreur d'import : ", "Import error: ") + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  const parseCsvToUnits = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    // Detect delimiter (comma, semicolon, or tab)
    const firstLine = lines[0];
    const delimiter = firstLine.includes(";") ? ";" : firstLine.includes("\t") ? "\t" : ",";
    const headers = lines[0].split(delimiter).map((h) => h.trim().toLowerCase().replace(/^["']|["']$/g, ""));
    const qIdx = headers.findIndex((h) => h.includes("question") || h === "q");
    const aIdx = headers.findIndex((h) => h.includes("reponse") || h.includes("réponse") || h.includes("answer") || h === "r");
    const tagIdx = headers.findIndex((h) => h.includes("tag") || h.includes("theme") || h.includes("thème"));
    const vidIdx = headers.findIndex((h) => h.includes("video"));
    if (qIdx < 0 || aIdx < 0) return [];
    return lines
      .slice(1)
      .map((line) => {
        // Simple CSV split (no escaping for now)
        const parts = line.split(delimiter).map((p) => p.trim().replace(/^["']|["']$/g, ""));
        return {
          question: parts[qIdx] || "",
          answer: parts[aIdx] || "",
          tags: tagIdx >= 0 && parts[tagIdx] ? parts[tagIdx].split(",").map((t) => t.trim()) : [],
          video_id: vidIdx >= 0 ? parts[vidIdx] : undefined,
        };
      })
      .filter((u) => u.question && u.answer);
  };

  const handleImport = async () => {
    let units = [];
    try {
      if (tab === "csv") {
        units = parseCsvToUnits(csvText);
        if (units.length === 0) {
          toast.error(tr("Aucune Q&R valide trouvee. Vérifiez les colonnes 'question' et 'reponse'.", "No valid Q&A found. Check the 'question' and 'reponse' columns."));
          return;
        }
      } else {
        const parsed = JSON.parse(jsonText);
        units = Array.isArray(parsed) ? parsed : parsed.units || [];
        if (units.length === 0) {
          toast.error(tr("Le JSON ne contient pas de Q&R", "The JSON holds no Q&A"));
          return;
        }
      }
    } catch (e) {
      toast.error(tr("Erreur de parsing : ", "Parsing error: ") + e.message);
      return;
    }

    setImporting(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/api/knowledge/import-bulk`,
        {
          units,
          module_name: moduleName,
          video_id: videoId || undefined,
        },
        { withCredentials: true }
      );
      setResult(r.data);
      toast.success(tr(`${r.data.inserted} Q&R importées, ${r.data.skipped} ignorées`, `${r.data.inserted} Q&A imported, ${r.data.skipped} skipped`));
    } catch (e) {
      toast.error(tr("Erreur d'import : ", "Import error: ") + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  // i18n-donnee: exemples du FICHIER que le client importera — le format et
  // le contenu sont ses données, pas de l'interface.
  const csvExample = `question;reponse;tags;video_id
Qu'est-ce qu'un REER?;Un REER est un regime enregistre d'epargne-retraite qui permet de cotiser et deduire les cotisations du revenu imposable.;REER,fiscalite;
Quelle est la difference entre CELI et REER?;Le CELI offre une croissance libre d'impot mais aucune deduction; le REER offre une deduction immediate mais l'argent est impose au retrait.;CELI,REER,fiscalite;
Quel est le plafond du CELI en 2026?;Le plafond annuel du CELI est de 7000$ en 2026, et le plafond cumulatif depuis 2009 est de 102000$.;CELI,plafonds;`;

  // i18n-donnee: même raison que csvExample.
  const jsonExample = `[
  {
    "question": "Qu'est-ce qu'un REER?",
    "answer": "Un REER est un regime enregistre d'epargne-retraite...",
    "tags": ["REER", "fiscalite"]
  },
  {
    "question": "Quelle est la difference entre CELI et REER?",
    "answer": "Le CELI offre une croissance libre d'impot...",
    "tags": ["CELI", "REER"]
  }
]`;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">{tr("Import en lot de Q&R", "Bulk Q&A import")}</h1>
        <p className="text-muted-foreground">
          {tr("Importez plusieurs questions/réponses d'un coup depuis un fichier CSV (Google Sheets) ou JSON.", "Import several questions and answers at once from a CSV (Google Sheets) or JSON file.")}
        </p>
      </div>

      {/* Info banner */}
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start gap-3">
          <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900 dark:text-blue-200">
            <p className="font-semibold mb-1">{tr("Format attendu", "Expected format")}</p>
            <p className="text-xs leading-relaxed">
              <strong>CSV</strong>{tr(" : 1re ligne = en-têtes (", ": first row = headers (")}<code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">question</code>, <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">reponse</code>, <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">tags</code>{tr(" optionnel, ", " optional, ")}<code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">video_id</code>{tr(" optionnel). Séparateur : ", " optional). Separator: ")}<strong>;</strong>{tr(" ou ", " or ")}<strong>,</strong>{tr(" ou ", " or ")}<strong>tab</strong>.
              <br />
              <strong>JSON</strong>{tr(" : tableau d'objets avec les champs ", ": array of objects with the fields ")}<code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">question</code>{tr(" et ", " and ")}<code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">answer</code>.
            </p>
          </div>
        </div>
      </div>

      {/* Module name + video_id */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-semibold mb-2" data-testid="label-module">
            {tr("Nom du module", "Module name")}
          </label>
          <input
            type="text"
            value={moduleName}
            onChange={(e) => setModuleName(e.target.value)}
            className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
            placeholder={tr("Module 1 - Connaissances générales", "Module 1 - General knowledge")}
            data-testid="input-module-name"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2">
            {tr("ID vidéo par défaut ", "Default video ID ")}<span className="text-muted-foreground text-xs">{tr("(optionnel)", "(optional)")}</span>
          </label>
          <input
            type="text"
            value={videoId}
            onChange={(e) => setVideoId(e.target.value)}
            className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:ring-2 focus:ring-[#002FA7] focus:border-transparent font-mono text-sm"
            placeholder="ex: vid-abc123"
            data-testid="input-default-video-id"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border mb-4">
        <button
          onClick={() => setTab("file")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            tab === "file" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="tab-file"
        >
          <CloudArrowUp size={16} className="inline mr-2" />
          {tr("Fichier .csv (recommandé)", ".csv file (recommended)")}
        </button>
        <button
          onClick={() => setTab("csv")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            tab === "csv" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="tab-csv"
        >
          <FileCsv size={16} className="inline mr-2" />
          {tr("Coller du CSV", "Paste CSV")}
        </button>
        <button
          onClick={() => setTab("json")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            tab === "json" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="tab-json"
        >
          { } JSON
        </button>
      </div>

      {/* Éditeur */}
      {tab === "file" ? (
        <div className="space-y-4">
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center bg-card">
            <CloudArrowUp size={40} className="mx-auto mb-3 text-[#002FA7]" />
            <p className="text-sm text-muted-foreground mb-3">
              {tr("Glissez un fichier .csv ici ou cliquez pour choisir", "Drop a .csv file here, or click to pick one")}
            </p>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              className="mx-auto"
              data-testid="file-input-csv"
            />
            {csvFile && (
              <p className="mt-3 text-xs text-teal-700 font-semibold">
                ✓ {csvFile.name} ({(csvFile.size / 1024).toFixed(1)} Ko)
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <button
              onClick={downloadTemplate}
              className="text-[#002FA7] hover:underline font-semibold"
              data-testid="btn-download-template"
            >
              {tr("📥 Télécharger le modèle CSV", "📥 Download the CSV template")}
            </button>
            <span className="opacity-40">•</span>
            <span>{tr("Colonnes : ", "Columns: ")}<code>titre, question, reponse, theme, categorie, mots_cles</code></span>
          </div>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
            <p className="font-semibold mb-1">{tr("ℹ️ Avantages de cette méthode", "ℹ️ What this method gives you")}</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>{tr("Embeddings automatiquement générés (recherche RAG activée)", "Embeddings generated automatically (RAG search enabled)")}</li>
              <li>{tr("Déduplication par question (évite les doublons)", "Deduplication by question (no duplicates)")}</li>
              <li>{tr("Détection automatique du séparateur (", "Separator detected automatically (")}<code>,</code>, <code>;</code>, <code>|</code>, tab)</li>
              <li>{tr("Accents UTF-8 préservés (même si exporté depuis Excel)", "UTF-8 accents preserved (even when exported from Excel)")}</li>
              <li>{tr("Q&R importées directement en statut ", "Q&A imported straight into status ")}<strong>approved</strong></li>
            </ul>
          </div>
        </div>
      ) : tab === "csv" ? (
        <div>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={csvExample}
            className="w-full h-72 px-4 py-3 bg-card border border-border rounded-lg font-mono text-xs focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
            data-testid="textarea-csv"
          />
          <button
            onClick={() => setCsvText(csvExample)}
            className="text-xs text-[#002FA7] hover:underline mt-2"
            data-testid="btn-load-csv-example"
          >
            {tr("Charger un exemple", "Load an example")}
          </button>
        </div>
      ) : (
        <div>
          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder={jsonExample}
            className="w-full h-72 px-4 py-3 bg-card border border-border rounded-lg font-mono text-xs focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
            data-testid="textarea-json"
          />
          <button
            onClick={() => setJsonText(jsonExample)}
            className="text-xs text-[#002FA7] hover:underline mt-2"
            data-testid="btn-load-json-example"
          >
            {tr("Charger un exemple", "Load an example")}
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 mt-6">
        <Button
          onClick={tab === "file" ? handleFileImport : handleImport}
          disabled={importing || (tab === "file" ? !csvFile : tab === "csv" ? !csvText.trim() : !jsonText.trim())}
          className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
          data-testid="btn-import-knowledge"
        >
          <CloudArrowUp size={18} className="mr-2" />
          {importing ? tr("Import en cours…", "Importing…") : tr("Importer les Q&R", "Import the Q&A")}
        </Button>
        <Button variant="outline" onClick={() => navigate("/dashboard/knowledge")} data-testid="btn-cancel-import">
          {tr("Annuler", "Cancel")}
        </Button>
      </div>

      {/* Result */}
      {result && (
        <div className="mt-8 p-5 bg-card border border-border rounded-lg" data-testid="import-result">
          <h3 className="font-bold mb-3 flex items-center gap-2">
            <CheckCircle size={20} className="text-blue-600" />
            {tr("Résultat de l'import", "Import result")}
          </h3>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
              <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{result.inserted}</div>
              <div className="text-xs text-blue-600 uppercase tracking-wider">{tr("Importées", "Imported")}</div>
            </div>
            <div className="text-center p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">{result.skipped}</div>
              <div className="text-xs text-amber-600 uppercase tracking-wider">{tr("Ignorées", "Skipped")}</div>
            </div>
          </div>
          {result.errors && result.errors.length > 0 && (
            <details className="mt-3">
              <summary className="text-sm cursor-pointer text-muted-foreground hover:text-foreground">
                <XCircle size={14} className="inline mr-1 text-red-500" />
                {result.errors.length} {tr("erreur(s)", "error(s)")}
              </summary>
              <ul className="mt-2 text-xs text-red-700 dark:text-red-400 space-y-1 max-h-40 overflow-y-auto">
                {result.errors.map((err, i) => (
                  <li key={i} className="font-mono">{err}</li>
                ))}
              </ul>
            </details>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/dashboard/knowledge")}
            className="mt-4"
            data-testid="btn-view-knowledge"
          >
            {tr("Voir la base de connaissances", "Open the knowledge base")}
          </Button>
        </div>
      )}
    </div>
  );
}
